import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../config/firebase";
import { getStaffProvisioningEnvironment } from "../config/staffProvisioningEnvironment";
import StaffDeviceEnrollmentCode from "./StaffDeviceEnrollmentCode";
import "./styles/StaffDeviceControls.css";

const record = value => value !== null && typeof value === "object" && !Array.isArray(value);
const deviceId = value => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
const validCursor = (cursor, restaurantId, projectId) => record(cursor) && Object.keys(cursor).length === 4 &&
	cursor.version === 1 && cursor.restaurantId === restaurantId && cursor.projectId === projectId && deviceId(cursor.afterDeviceId);
const matchesScope = (value, restaurantId, projectId) =>
	(value.restaurantId === undefined || value.restaurantId === restaurantId) &&
	(value.projectId === undefined || value.projectId === projectId);

function readDevicePage(value, restaurantId, projectId, after) {
	const invalid = () => { throw new Error("Invalid device inventory response"); };
	if (!record(value) || value.success !== true || !matchesScope(value, restaurantId, projectId) ||
		!Array.isArray(value.devices) || value.devices.length > 100 ||
		(value.truncated !== undefined && typeof value.truncated !== "boolean")) invalid();
	let previous = after?.afterDeviceId || "";
	const devices = value.devices.map(device => {
		if (!record(device) || !deviceId(device.deviceId) || device.deviceId <= previous ||
			!matchesScope(device, restaurantId, projectId) ||
			(device.accountId !== undefined && device.accountId !== restaurantId) ||
			typeof device.label !== "string" || !device.label.trim() || device.label.length > 80 ||
			typeof device.revoked !== "boolean" || !["until_revoked", "expires"].includes(device.enrollmentPolicy) ||
			!(device.expiresAt === null || (Number.isSafeInteger(device.expiresAt) && device.expiresAt >= 0)) ||
			(device.enrollmentPolicy === "until_revoked" && device.expiresAt !== null) ||
			!(device.replacedBy === null || deviceId(device.replacedBy)) ||
			!(device.removed === undefined || typeof device.removed === "boolean") ||
			!(device.createdAt == null || (Number.isSafeInteger(device.createdAt) && device.createdAt >= 0))) invalid();
		previous = device.deviceId;
		return { deviceId: device.deviceId, label: device.label, revoked: device.revoked,
			enrollmentPolicy: device.enrollmentPolicy, expiresAt: device.expiresAt, replacedBy: device.replacedBy,
			removed: device.removed === true, createdAt: device.createdAt ?? null };
	});
	// Keep the existing per-device projection and legacy truncated-only response;
	// neither requires new tenant fields or a support-operation envelope.
	const paginated = value.hasMore !== undefined || value.nextCursor !== undefined;
	if (paginated && (typeof value.hasMore !== "boolean" ||
		(value.truncated !== undefined && value.truncated !== value.hasMore) ||
		(value.hasMore ? !validCursor(value.nextCursor, restaurantId, projectId) || !devices.length ||
			value.nextCursor.afterDeviceId !== previous : value.nextCursor !== null))) invalid();
	return { devices, nextCursor: paginated ? value.nextCursor : null, incomplete: !paginated && value.truncated === true };
}

// Removed and replaced enrollments are history; the list leads with devices still in use.
const retired = device => device.removed || (device.revoked && device.replacedBy !== null);
const addedOn = device => device.createdAt ? new Date(device.createdAt).toLocaleDateString() : "";

export default function StaffDeviceControls({ restaurantId }) {
	const [user, loading, error] = useAuthState(auth);
	const actorScope = useRef({ user: null, version: 0 });
	if (actorScope.current.user !== user) actorScope.current = { user, version: actorScope.current.version + 1 };
	const environmentLabel = getStaffProvisioningEnvironment();
	const projectId = functions.app.options.projectId;
	if (!environmentLabel || !restaurantId) return null;
	if (loading) return <p role="status">Checking support access...</p>;
	if (error || !user || auth.currentUser !== user) return <p role="alert">Sign in with an authorized support account.</p>;
	return <ScopedDeviceControls key={JSON.stringify([restaurantId, environmentLabel, projectId, user.uid, actorScope.current.version])}
		restaurantId={restaurantId} environmentLabel={environmentLabel} projectId={projectId} instance={functions} user={user} actorUid={user.uid} />;
}

function ScopedDeviceControls({ restaurantId, environmentLabel, projectId, instance, user, actorUid }) {
	const [devices, setDevices] = useState([]);
	const [nextCursor, setNextCursor] = useState(null);
	const [loading, setLoading] = useState(false);
	const [listError, setListError] = useState("");
	const [label, setLabel] = useState("");
	const [credential, setCredential] = useState(null);
	const manualEnrollment = useRef(null);
	const [message, setMessage] = useState("");
	const [rotationPreview, setRotationPreview] = useState(null);
	const [busy, setBusy] = useState(false);
	const [replacing, setReplacing] = useState(null);
	const [showRetired, setShowRetired] = useState(false);
	const pending = useRef(false);
	const lifecycle = useRef(0);
	const alive = useRef(false);
	const listRequest = useRef(0);
	const paging = useRef(false);
	const isCurrent = useCallback((generation = lifecycle.current) => alive.current && lifecycle.current === generation &&
		auth.currentUser === user && auth.currentUser?.uid === actorUid &&
		instance === functions && functions.app.options.projectId === projectId &&
		getStaffProvisioningEnvironment() === environmentLabel, [instance, projectId, environmentLabel, user, actorUid]);
	const refresh = useCallback(async (cursor = null) => {
		const generation = lifecycle.current;
		if (!isCurrent(generation) || (cursor && paging.current)) return;
		const request = ++listRequest.current;
		paging.current = true; setLoading(true); setListError("");
		if (!cursor) { setDevices([]); setNextCursor(null); setRotationPreview(null); }
		try {
			const result = await httpsCallable(instance, "listScervStaffDevices")({ restaurantId, ...(cursor ? { cursor } : {}) });
			if (!isCurrent(generation) || request !== listRequest.current) return;
			const page = readDevicePage(result.data, restaurantId, projectId, cursor);
			setDevices(previous => cursor ? [...previous, ...page.devices] : page.devices);
			setNextCursor(page.nextCursor);
			if (page.incomplete) {
				setListError("Inventory is incomplete. This backend does not provide a continuation cursor.");
			}
		} catch {
			if (isCurrent(generation) && request === listRequest.current) setListError("Unable to load enrolled devices.");
		} finally {
			if (isCurrent(generation) && request === listRequest.current) { paging.current = false; setLoading(false); }
		}
	}, [isCurrent, instance, restaurantId, projectId]);
	useEffect(() => {
		const generation = ++lifecycle.current;
		alive.current = true;
		refresh();
		return () => { alive.current = false; lifecycle.current = generation + 1; paging.current = false; };
	}, [refresh]);
	useEffect(() => {
		if (!credential) return undefined;
		if (manualEnrollment.current) manualEnrollment.current.open = true;
		const timer = setTimeout(() => setCredential(null), 5 * 60 * 1000);
		return () => clearTimeout(timer);
	}, [credential]);
	const execute = async (endpoint, data, deviceLabel) => {
		const generation = lifecycle.current;
		if (!isCurrent(generation) || pending.current) return;
		pending.current = true; setBusy(true); setCredential(null); setMessage(""); setRotationPreview(null);
		listRequest.current++; paging.current = false; setLoading(false);
		let submitted = false;
		const acceptPreview = preview => {
			if (!preview || preview.restaurantId !== restaurantId || preview.deviceId !== data.deviceId ||
				typeof preview.canRotate !== "boolean" || typeof preview.complete !== "boolean" || !Array.isArray(preview.blockers)) {
				throw new Error("Rotation preview unavailable");
			}
			setRotationPreview(preview);
			return preview.canRotate && preview.complete && preview.blockers.length === 0;
		};
		try {
			if (endpoint === "rotateScervStaffDevice") {
				const result = await httpsCallable(instance, "listScervStaffDevices")({ restaurantId, rotationDeviceId: data.deviceId });
				if (!isCurrent(generation)) return;
				if (!acceptPreview(result.data?.rotationPreview)) { setMessage("Rotation blocked."); return; }
				if (!window.confirm(`Rotate ${deviceLabel}? No blocking hardware dependencies were found. Its current credential and staff sessions will stop working.`)) return;
			}
			if (!isCurrent(generation)) return;
			submitted = true;
			const result = await httpsCallable(instance, endpoint)({ restaurantId, ...data });
			if (!isCurrent(generation)) return;
			if (!result.data?.success) throw new Error("Unconfirmed operation");
			if (result.data.deviceToken) setCredential({ deviceToken: result.data.deviceToken, expiresAt: result.data.expiresAt });
			if (replacing?.deviceId === data.deviceId) setReplacing(null);
			setMessage("Completed.");
			await refresh();
		} catch (error) {
			if (!isCurrent(generation)) return;
			const preview = error.details?.rotationPreview;
			if (endpoint === "rotateScervStaffDevice" && preview?.restaurantId === restaurantId &&
				preview.deviceId === data.deviceId && Array.isArray(preview.blockers)) {
				setRotationPreview(preview); setMessage("Rotation blocked. Hardware dependencies changed or remain unresolved.");
			} else if (!submitted) setMessage("Unable to preview rotation. No rotation was requested.");
			else setMessage("Could not confirm the operation. Refresh the inventory before retrying; an enrollment may have been created or rotated.");
		} finally { if (isCurrent(generation)) { pending.current = false; setBusy(false); } }
	};
	const visible = showRetired ? devices : devices.filter(device => !retired(device));
	const hiddenCount = devices.length - devices.filter(device => !retired(device)).length;
	const startReplacing = device => { setReplacing(device); setLabel(device.label); setMessage(""); };
	return <section className="restaurant-section staff-device-controls">
		<h2>Staff devices</h2>
		<p>{environmentLabel} environment</p>
		{replacing && <p role="status" className="staff-device-replacing">
			The next code replaces <strong>{replacing.label}</strong>. When the tablet enrolls, the old enrollment stops working and
			its drawer pairing moves over (unless a drawer command is still open on it).{" "}
			<button type="button" disabled={busy} onClick={() => setReplacing(null)}>Don&apos;t replace</button>
		</p>}
		<label>Device label <input maxLength={80} value={label} onChange={(event) => setLabel(event.target.value)} disabled={busy} /></label>
		<StaffDeviceEnrollmentCode restaurantId={restaurantId} projectId={projectId} instance={instance}
			label={label} disabled={busy} isCurrent={isCurrent} replacesDeviceId={replacing?.deviceId || null} />
		<details ref={manualEnrollment}>
			<summary>Advanced: manual enrollment</summary>
			<button type="button" disabled={busy || !label.trim()} onClick={() => execute("enrollScervStaffDevice", { label: label.trim() })}>Enroll device</button>
			{credential && <div>
				<label>Enrollment credentials<textarea readOnly autoComplete="off" spellCheck={false} value={JSON.stringify(credential)} style={{ display: "block", width: "100%", minHeight: 100, boxSizing: "border-box" }} /></label>
				<button type="button" onClick={() => setCredential(null)}>Dismiss credentials</button>
			</div>}
		</details>
		<button type="button" disabled={busy} onClick={() => refresh()}>Refresh</button>
		{message && <p role="status">{message}</p>}
		{listError && <p role="alert">{listError}</p>}
		{rotationPreview?.blockers.length > 0 && <ul aria-label="Rotation blockers">{rotationPreview.blockers.map(blocker => <li key={blocker.code}>{blocker.message}</li>)}</ul>}
		<div style={{ overflowX: "auto" }}><table className="restaurant-table">
			<thead><tr><th>Device</th><th>Added</th><th>Enrollment ID</th><th>Status</th><th>Actions</th></tr></thead>
			<tbody>{visible.map((device) => <tr key={device.deviceId}>
				<td>{device.label}</td><td>{addedOn(device)}</td><td style={{ overflowWrap: "anywhere", maxWidth: 240 }}>{device.deviceId}</td>
				<td>{device.removed ? "Removed" : device.revoked && device.replacedBy ? "Replaced" : device.revoked ? "Revoked" : device.enrollmentPolicy === "until_revoked" ? "Enrolled until revoked" : device.expiresAt <= Date.now() ? "Expired" : "Enrolled"}</td>
				<td><button type="button" disabled={busy || loading || device.revoked} onClick={() => execute("rotateScervStaffDevice", { deviceId: device.deviceId }, device.label)}>Rotate</button>
				<button type="button" disabled={busy || device.revoked} onClick={() => {
					if (window.confirm(`Revoke ${device.label}? Staff sessions on this enrollment will stop working.`)) execute("revokeScervStaffDevice", { deviceId: device.deviceId });
				}}>Revoke</button>
				<button type="button" disabled={busy || device.replacedBy !== null || device.removed || replacing?.deviceId === device.deviceId}
					onClick={() => startReplacing(device)}>Reinstall / replace</button>
				<button type="button" disabled={busy || device.removed} onClick={() => {
					if (window.confirm(device.revoked ? `Remove ${device.label} from this list?`
						: `Remove ${device.label}? It stops working right away and disappears from this list.`)) {
						execute("removeScervStaffDevice", { deviceId: device.deviceId });
					}
				}}>Remove</button></td>
			</tr>)}</tbody>
		</table></div>
		{hiddenCount > 0 && <button type="button" onClick={() => setShowRetired(!showRetired)}>
			{showRetired ? "Hide removed and replaced devices" : `Show removed and replaced devices (${hiddenCount})`}</button>}
		{loading && <p role="status">Loading devices...</p>}
		{nextCursor && <button type="button" disabled={busy || loading} onClick={() => refresh(nextCursor)}>Load more</button>}
	</section>;
}
